import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../core/services/supabase';
import { useAuth } from '../core/contexts/AuthContext';

export default function NotificationBanner() {
  const { t } = useTranslation(['common', 'dashboard']);
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadNotifications();

      // Set up real-time subscription for new notifications
      const channel = supabase
        .channel('internal_notifications_changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'internal_notifications'
          },
          () => {
            loadNotifications();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    } else {
      setLoading(false);
    }
  }, [user]);

  async function loadNotifications() {
    try {
      // Get user's role and team
      const { data: userRoles, error: rolesError } = await supabase
        .from('user_roles')
        .select('role, team_id')
        .eq('user_id', user.id)
        .eq('is_active', true);

      if (rolesError) throw rolesError;

      if (!userRoles || userRoles.length === 0) {
        setNotifications([]);
        setLoading(false);
        return;
      }

      const role = userRoles[0].role;
      const teamId = userRoles[0].team_id;

      // Validate role is one of the expected values (whitelist)
      const validRoles = ['system_admin', 'club_admin', 'team_captain', 'player'];
      if (!validRoles.includes(role)) {
        console.error('Invalid role detected:', role);
        setNotifications([]);
        setLoading(false);
        return;
      }

      // Get notifications for this user's role and team
      // Note: .or() requires string format. Role is validated above and sourced from database,
      // making this interpolation safe from SQL injection.
      let query = supabase
        .from('internal_notifications')
        .select(`
          *,
          match:matches(opponent_name, match_date)
        `)
        .eq('team_id', teamId)
        .or(`target_role.eq.all,target_role.eq.${role}`)
        .order('created_at', { ascending: false });

      const { data: notifData, error: notifError } = await query;

      if (notifError) throw notifError;

      // Get user's dismissals
      const { data: dismissals, error: dismissError } = await supabase
        .from('notification_dismissals')
        .select('notification_id')
        .eq('user_id', user.id);

      if (dismissError) throw dismissError;

      const dismissedIds = new Set(dismissals?.map(d => d.notification_id) || []);
      const now = new Date();

      // Filter out dismissed and expired notifications
      const activeNotifications = (notifData || []).filter(n => {
        if (dismissedIds.has(n.id)) return false;
        if (n.expires_at && new Date(n.expires_at) < now) return false;
        return true;
      });

      setNotifications(activeNotifications);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  }

  async function dismissNotification(notificationId) {
    try {
      const { error } = await supabase
        .from('notification_dismissals')
        .insert({
          notification_id: notificationId,
          user_id: user.id
        });

      if (error) throw error;

      // Remove from local state
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error('Error dismissing notification:', error);
    }
  }

  function getSeverityStyles(severity) {
    switch (severity) {
      case 'urgent':
        return 'bg-red-100 border-red-400 text-red-900';
      case 'warning':
        return 'bg-yellow-100 border-yellow-400 text-yellow-900';
      case 'info':
      default:
        return 'bg-blue-100 border-blue-400 text-blue-900';
    }
  }

  function getSeverityIcon(severity) {
    switch (severity) {
      case 'urgent':
        return '🚨';
      case 'warning':
        return '⚠️';
      case 'info':
      default:
        return 'ℹ️';
    }
  }

  if (loading || notifications.length === 0) {
    return null;
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-50 space-y-2 p-4">
      {notifications.map(notification => (
        <div
          key={notification.id}
          className={`rounded-lg border-2 shadow-lg p-4 ${getSeverityStyles(notification.severity)}`}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3 flex-1">
              <span className="text-2xl flex-shrink-0">
                {getSeverityIcon(notification.severity)}
              </span>
              <div className="flex-1">
                <h3 className="font-bold text-lg mb-1">{notification.title}</h3>
                <p className="text-sm">{notification.message}</p>
                {notification.match && (
                  <p className="text-xs mt-2 opacity-75">
                    {t('common:text.match')}: {notification.match.opponent_name} ({new Date(notification.match.match_date).toLocaleDateString()})
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={() => dismissNotification(notification.id)}
              className="ml-4 text-2xl font-bold opacity-50 hover:opacity-100 transition-opacity flex-shrink-0"
              aria-label={t('common:button.dismiss')}
            >
              ×
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
